import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useEditorStore } from '../../store/useEditorStore';
import { Point2D, ToolType } from '../../types';
import { snapToGrid, snapToAngle, snapToValidArea } from '../../utils/geometry';
import { getPointNearEndpoint, isPointOnElement } from '../../utils/geometryHelpers';
import { snapToWall } from '../../utils/wallHelpers';
import { loadModel3DOutline, drawModel3DOutline } from '../../utils/model3DPreview';
import { renderGrid } from './renderers/GridRenderer';
import { renderBaseMap } from './renderers/BaseMapRenderer';
import { renderElements } from './renderers/ElementRenderer';
import { renderTempPoints } from './renderers/TempPointsRenderer';
import WallPropertyPanel from './WallPropertyPanel';
import AreaPropertyPanel from './AreaPropertyPanel';
import FurniturePropertyPanel from './FurniturePropertyPanel';
import CirclePropertyPanel from './CirclePropertyPanel';
import PolygonPropertyPanel from './PolygonPropertyPanel';
import DoorPropertyPanel from './DoorPropertyPanel';
import StairPropertyPanel from './StairPropertyPanel';
import TextInputDialog from '../dialogs/TextInputDialog';
import ConfirmDialog from './ConfirmDialog';
import ContextMenu, { createElementContextMenu, createCanvasContextMenu } from './ContextMenu';
import MeasurementOverlay from './MeasurementOverlay';
import { useTranslation } from 'react-i18next';

interface Canvas2DProps {
  width: number;
  height: number;
  onZoomChange?: (zoom: number) => void;
  onOffsetChange?: (offset: Point2D) => void;
}

// 网格吸附大小（像素）- 减小以提供更精细的控制
const GRID_SNAP_SIZE = 2;

// 浮窗尺寸配置
const FLOATING_PANEL_WIDTH = 180;
const FLOATING_PANEL_WIDTH_SMALL = 160; // AreaPropertyPanel 宽度
const FLOATING_PANEL_HEIGHT_WALL = 380; // 墙面浮窗高度
const FLOATING_PANEL_HEIGHT_FURNITURE = 260; // 家具浮窗高度
const FLOATING_PANEL_HEIGHT_CIRCLE = 180; // 圆形浮窗高度
const FLOATING_PANEL_HEIGHT_POLYGON = 140; // 多边形浮窗高度
const FLOATING_PANEL_HEIGHT_AREA = 80; // 区域浮窗高度
const FLOATING_PANEL_HEIGHT_DOOR = 200; // 门浮窗高度
const FLOATING_PANEL_HEIGHT_STAIR = 380; // 楼梯浮窗高度
const CONFIRM_DIALOG_WIDTH = 180; // 确认对话框宽度
const CONFIRM_DIALOG_HEIGHT = 80; // 确认对话框高度
const FLOATING_PANEL_OFFSET = 20; // 默认偏移量

/**
 * 调整浮窗位置，确保它不会超出画布边界
 * @param preferredX 期望的 X 坐标
 * @param preferredY 期望的 Y 坐标
 * @param panelWidth 浮窗宽度
 * @param panelHeight 浮窗高度
 * @param canvasWidth 画布宽度
 * @param canvasHeight 画布高度
 * @returns 调整后的位置
 */
const adjustPanelPosition = (
  preferredX: number,
  preferredY: number,
  panelWidth: number,
  panelHeight: number,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number } => {
  let x = preferredX;
  let y = preferredY;

  // 边界检测与调整
  const padding = 10; // 与画布边缘的最小间距

  // 右边界检查
  if (x + panelWidth + padding > canvasWidth) {
    x = canvasWidth - panelWidth - padding;
  }
  // 左边界检查
  if (x < padding) {
    x = padding;
  }

  // 下边界检查
  if (y + panelHeight + padding > canvasHeight) {
    y = canvasHeight - panelHeight - padding;
  }
  // 上边界检查
  if (y < padding) {
    y = padding;
  }

  return { x, y };
};

const Canvas2D: React.FC<Canvas2DProps> = ({ width, height, onZoomChange, onOffsetChange }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point2D>({ x: 0, y: 0 });
  const [offset, setOffset] = useState<Point2D>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [mouseWorldPos, setMouseWorldPos] = useState<Point2D | null>(null);
  // 旋转状态（占位，未实现完整功能）
  const [, setRotateState] = useState<{ elementId: string; startAngle: number; centerPoint: Point2D } | null>(null);
  const [dragState, setDragState] = useState<{ elementId: string; pointIndex: number } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; position: { x: number; y: number } } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; elementId: string | null } | null>(null);
  const [elementDragState, setElementDragState] = useState<{ elementId: string; startPoint: Point2D; originalPoints: Point2D[] } | null>(null);
  const [model3DOutline, setModel3DOutline] = useState<{ lines: { x: number; y: number }[][]; width: number; height: number } | null>(null);
  // 防止居中重复触发的标志
  const hasCenteredRef = useRef(false);

  const {
    project,
    currentTool,
    currentFloor,
    tempPoints,
    isDrawing,
    selectedElements,
    hoveredElement,
    wallProperties,
    hoveredPoint,
    pendingArea,
    pendingFurniture,
    pendingCircle,
    pendingPolygon,
    pendingDoor,
    doorWindowDrawing,
    pendingStair,
    pendingText,
    model3DPlacement,
    hiddenCategories,
    addTempPoint,
    updateLastTempPoint,
    finishDrawing,
    cancelDrawing,
    setHoveredElement,
    selectElement,
    clearSelection,
    setHoveredPoint,
    setRightPanelTab,
    updateElementPoint,
    setPendingArea,
    createAreaWithName,
    setPendingFurniture,
    createFurnitureWithProperties,
    setPendingCircle,
    createCircleWithProperties,
    setPendingPolygon,
    createPolygonWithProperties,
    setDoorWindowDrawing,
    setPendingDoor,
    createDoorWithProperties,
    setPendingStair,
    createStairWithProperties,
    setPendingText,
    createTextWithContent,
    copySelectedElements,
    cutSelectedElements,
    pasteElements,
    deleteSelectedElements,
    updateElement,
    clipboard,
    alignLeft,
    alignRight,
    alignTop,
    alignBottom,
    alignVerticalCenter,
    alignHorizontalCenter,
    // rotateElement, // 已禁用画布旋转功能
    selectMultipleElements,
    cancelModel3DPlacement,
    placeModel3D,
    isCalibrating,
    calibrationPoint1,
    setBaseMapCentered,
  } = useEditorStore();

  // 当底图变化时，重置居中标志
  useEffect(() => {
    hasCenteredRef.current = false;
  }, [project?.mapConfig?.baseMap?.id]);

  // 坐标转换：屏幕坐标 -> 世界坐标（2D Canvas）
  // ==========================================
  // 屏幕坐标：相对于canvas元素的像素坐标（考虑缩放和平移）
  // 世界坐标：2D编辑器的逻辑坐标（像素单位，未缩放）
  // 
  // 这个转换只处理canvas的视图变换（zoom和offset）
  // 不涉及到3D坐标系的转换
  // ==========================================
  const screenToWorld = useCallback(
    (screenX: number, screenY: number): Point2D => {
      return {
        x: (screenX - offset.x) / zoom,
        y: (screenY - offset.y) / zoom,
      };
    },
    [offset, zoom]
  );

  // 坐标转换：世界坐标 -> 屏幕坐标（2D Canvas）
  const worldToScreen = useCallback(
    (worldX: number, worldY: number): Point2D => {
      return {
        x: worldX * zoom + offset.x,
        y: worldY * zoom + offset.y,
      };
    },
    [offset, zoom]
  );

  // 渲染节流：限制渲染频率到60fps
  const lastRenderTime = useRef(0);
  const renderThrottled = useCallback(() => {
    const now = performance.now();
    const FPS_LIMIT = 60;
    const FRAME_INTERVAL = 1000 / FPS_LIMIT;
    
    if (now - lastRenderTime.current < FRAME_INTERVAL) {
      return; // 跳过这一帧
    }
    lastRenderTime.current = now;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 清空画布
    ctx.clearRect(0, 0, width, height);

    // 保存状态
    ctx.save();

    // 应用变换
    ctx.translate(offset.x, offset.y);
    ctx.scale(zoom, zoom);
    
    // 调试：每100帧输出一次当前的offset和zoom
    if (Math.random() < 0.01) {
      console.log('🖼️ 渲染状态:', { offset, zoom, hasBaseMap: !!project?.mapConfig?.baseMap });
    }

    // 1. 渲染网格
    renderGrid(ctx, width, height, zoom, offset);

    // 2. 渲染底图（CAD平面图）- 在世界坐标系中渲染
    const baseMap = project?.mapConfig?.baseMap;
    if (baseMap && baseMap.visible) {
      renderBaseMap(ctx, baseMap);
    }

    // 3. 渲染所有元素（在底图上描绘的）
    if (project && currentFloor) {
      const floor = project.floors.find((f) => f.id === currentFloor);
      if (floor) {
        // 计算当前视口边界（世界坐标）用于视口裁剪优化
        const viewportBounds = {
          minX: -offset.x / zoom,
          maxX: (width - offset.x) / zoom,
          minY: -offset.y / zoom,
          maxY: (height - offset.y) / zoom,
        };
        
        renderElements(ctx, floor, selectedElements, hoveredElement, hoveredPoint, hiddenCategories, viewportBounds, zoom);
      }
    }

    // 3.5. 渲染有效区域边界（在墙面工具激活时显示）
    const validArea = project?.mapConfig?.validArea;
    if (validArea && validArea.length >= 3 && currentTool === ToolType.WALL) {
      ctx.save();
      ctx.strokeStyle = '#bed1fe'; // 浅蓝色边界
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 5]); // 虚线
      ctx.globalAlpha = 0.8;
      
      ctx.beginPath();
      ctx.moveTo(validArea[0].x, validArea[0].y);
      for (let i = 1; i < validArea.length; i++) {
        ctx.lineTo(validArea[i].x, validArea[i].y);
      }
      ctx.closePath();
      ctx.stroke();
      
      ctx.restore();
    }

    // 4. 渲染临时点（正在描绘中）
    if (isDrawing && tempPoints.length > 0) {
      renderTempPoints(ctx, tempPoints, currentTool, wallProperties.thickness, zoom);
      
      // 绘制从最后一个点到鼠标位置的预览线（墙面/区域/多边形工具）
      if (
        mouseWorldPos &&
        (currentTool === ToolType.WALL || 
         currentTool === ToolType.AREA || 
         currentTool === ToolType.POLYGON)
      ) {
        const lastPoint = tempPoints[tempPoints.length - 1];
        let snappedMouse = snapToGrid(mouseWorldPos, GRID_SNAP_SIZE);
        
        // 墙面工具应用特殊吸附
        if (currentTool === ToolType.WALL) {
          // 优先吸附到有效区域边界
          const validArea = project?.mapConfig?.validArea;
          if (validArea && validArea.length >= 3) {
            const validAreaSnap = snapToValidArea(mouseWorldPos, validArea, 30 / zoom);
            if (validAreaSnap.snapped) {
              snappedMouse = validAreaSnap.point;
            }
          }
          
          // 应用角度吸附
          if (wallProperties.useCustomAngle && wallProperties.customAngle !== null) {
            // 使用自定义角度吸附
            const angleRad = (wallProperties.customAngle * Math.PI) / 180;
            const dx = snappedMouse.x - lastPoint.x;
            const dy = snappedMouse.y - lastPoint.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // 计算目标角度的坐标
            snappedMouse = {
              x: lastPoint.x + distance * Math.cos(angleRad),
              y: lastPoint.y + distance * Math.sin(angleRad),
            };
          } else {
            // 使用默认90度倍数±5度吸附
            snappedMouse = snapToAngle(lastPoint, snappedMouse, 5);
          }
        }
        
        if (currentTool === ToolType.WALL) {
          // 墙面预览线条 - 固定像素大小
          const scaleFactor = 1 / zoom;
          const baseLineWidth = wallProperties.thickness / 50;
          const lineWidth = baseLineWidth * scaleFactor;
          const outlineWidth = lineWidth + 4 * scaleFactor;

          // 先绘制浅色描边
          ctx.strokeStyle = '#9ca3af'; // 使用稍浅的描边
          ctx.lineWidth = outlineWidth;
          ctx.lineCap = 'butt'; // 直角端点
          ctx.lineJoin = 'miter';
          ctx.globalAlpha = 0.4; // 降低不透明度

          ctx.beginPath();
          ctx.moveTo(lastPoint.x, lastPoint.y);
          ctx.lineTo(snappedMouse.x, snappedMouse.y);
          ctx.stroke();

          // 绘制主线条
          ctx.strokeStyle = '#d1d5db'; // 浅灰色
          ctx.lineWidth = lineWidth;
          ctx.globalAlpha = 0.6;
          ctx.setLineDash([5 * scaleFactor, 5 * scaleFactor]);

          ctx.beginPath();
          ctx.moveTo(lastPoint.x, lastPoint.y);
          ctx.lineTo(snappedMouse.x, snappedMouse.y);
          ctx.stroke();

          ctx.setLineDash([]);
          ctx.globalAlpha = 1;

          // 绘制鼠标位置的预览点（固定像素大小）
          const pointRadius = 5 * scaleFactor;
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(snappedMouse.x, snappedMouse.y, pointRadius, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#bed1fe';
          ctx.beginPath();
          ctx.arc(snappedMouse.x, snappedMouse.y, pointRadius * 0.7, 0, Math.PI * 2);
          ctx.fill();
        } else {
          // 其他工具的预览线条 - 固定像素大小
          const scaleFactor = 1 / zoom;
          const lineWidth = 2 * scaleFactor;

          ctx.strokeStyle = '#1e5a8a';
          ctx.lineWidth = lineWidth;
          ctx.setLineDash([5 * scaleFactor, 5 * scaleFactor]);
          ctx.globalAlpha = 0.5;

          ctx.beginPath();
          ctx.moveTo(lastPoint.x, lastPoint.y);
          ctx.lineTo(snappedMouse.x, snappedMouse.y);
          ctx.stroke();

          ctx.setLineDash([]);
          ctx.globalAlpha = 1;

          // 绘制鼠标位置的预览点（固定像素大小）
          const pointRadius = 5 * scaleFactor;
          const strokeWidth = 2 * scaleFactor;
          ctx.fillStyle = '#1e5a8a';
          ctx.beginPath();
          ctx.arc(snappedMouse.x, snappedMouse.y, pointRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = strokeWidth;
          ctx.stroke();
        }
        
        // 检查鼠标是否靠近起点（至少需要3个点）
        if (tempPoints.length >= 3) {
          const firstPoint = tempPoints[0];
          const dx = snappedMouse.x - firstPoint.x;
          const dy = snappedMouse.y - firstPoint.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // 如果鼠标靠近起点
          if (distance < 20 / zoom) {
            // 添加缩放因子，保持固定像素大小
            const scaleFactor = 1 / zoom;

            // 高亮显示起点
            ctx.strokeStyle = '#10b981'; // 绿色高亮
            ctx.lineWidth = 3 * scaleFactor;
            ctx.globalAlpha = 0.8;
            ctx.beginPath();
            ctx.arc(firstPoint.x, firstPoint.y, 10 * scaleFactor, 0, Math.PI * 2);
            ctx.stroke();

            ctx.fillStyle = '#10b981';
            ctx.globalAlpha = 0.3;
            ctx.fill();

            ctx.globalAlpha = 1;

            // 显示提示文字
            ctx.save();
            ctx.font = `${12 * scaleFactor}px Arial`;
            const text = t('canvas.clickToFinish');
            const textMetrics = ctx.measureText(text);
            const padding = 6 * scaleFactor;
            const bgWidth = textMetrics.width + padding * 2;
            const bgHeight = 20 * scaleFactor;

            // 背景
            ctx.fillStyle = 'rgba(16, 185, 129, 0.9)';
            ctx.fillRect(
              firstPoint.x - bgWidth / 2,
              firstPoint.y - 25 * scaleFactor,
              bgWidth,
              bgHeight
            );

            // 文字
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, firstPoint.x, firstPoint.y - 15 * scaleFactor);
            ctx.restore();
          }
        }
      }
    }
    
    // 门工具 - 绘制吸附点和预览（包括pending状态）
    if ((mouseWorldPos || pendingDoor) && currentTool === ToolType.DOOR) {
      if (!project || !currentFloor) return;
      const floor = project.floors.find(f => f.id === currentFloor);
      if (floor) {
        // 获取所有墙面
        const walls: any[] = [];
        for (const layer of floor.layers) {
          if (!layer.visible) continue;
          for (const element of layer.elements) {
            if (element.type === 'wall') {
              walls.push(element);
            }
          }
        }
        
        const color = '#8b5cf6'; // 紫色
        
        // 如果有pending状态（属性浮窗显示中），绘制完整的门线段
        const currentPending = pendingDoor;
        if (currentPending) {
          // 绘制确定的门窗线段（实线）
          ctx.strokeStyle = color;
          ctx.lineWidth = 4;
          ctx.globalAlpha = 0.8;
          
          ctx.beginPath();
          ctx.moveTo(currentPending.point1.x, currentPending.point1.y);
          ctx.lineTo(currentPending.point2.x, currentPending.point2.y);
          ctx.stroke();
          
          ctx.globalAlpha = 1;
          
          // 绘制两个端点
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(currentPending.point1.x, currentPending.point1.y, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(currentPending.point2.x, currentPending.point2.y, 5, 0, Math.PI * 2);
          ctx.fill();
        } else if (mouseWorldPos) {
          // 没有pending状态，显示吸附点和预览
          const snapResult = snapToWall(mouseWorldPos, walls, 30 / zoom);
          if (snapResult) {
            if (doorWindowDrawing) {
              // 已经点击了第一个点，显示预览线段
              if (snapResult.wallId === doorWindowDrawing.wallId && 
                  snapResult.wallSegmentIndex === doorWindowDrawing.wallSegmentIndex) {
                // 获取墙面线段的起点和终点
                let wall: any = null;
                for (const layer of floor.layers) {
                  if (!layer.visible) continue;
                  for (const element of layer.elements) {
                    if (element.id === snapResult.wallId && element.type === 'wall') {
                      wall = element;
                      break;
                    }
                  }
                  if (wall) break;
                }
                
                if (wall && wall.points.length > snapResult.wallSegmentIndex + 1) {
                  const wallP1 = wall.points[snapResult.wallSegmentIndex];
                  const wallP2 = wall.points[snapResult.wallSegmentIndex + 1];
                  
                  // 限制门窗两个点都在墙面线段范围内
                  const start = doorWindowDrawing.startPoint;
                  const end = snapResult.snappedPoint;
                  
                  // 确保两点都在墙面线段内
                  let clampedEnd = { ...end };
                  const dx = wallP2.x - wallP1.x;
                  const dy = wallP2.y - wallP1.y;
                  const segmentLenSq = dx * dx + dy * dy;
                  
                  // 计算end点在墙面上的投影参数
                  const t = ((end.x - wallP1.x) * dx + (end.y - wallP1.y) * dy) / segmentLenSq;
                  if (t < 0 || t > 1) {
                    clampedEnd = t < 0 ? wallP1 : wallP2;
                  }
                  
                  // 绘制预览线段（粗线）
                  ctx.strokeStyle = color;
                  ctx.lineWidth = 4;
                  ctx.globalAlpha = 0.7;
                  
                  ctx.beginPath();
                  ctx.moveTo(start.x, start.y);
                  ctx.lineTo(clampedEnd.x, clampedEnd.y);
                  ctx.stroke();
                  
                  ctx.globalAlpha = 1;
                  
                  // 绘制起点（实心圆）
                  ctx.fillStyle = color;
                  ctx.beginPath();
                  ctx.arc(start.x, start.y, 5, 0, Math.PI * 2);
                  ctx.fill();
                  
                  // 绘制当前吸附点（带描边的圆）
                  ctx.fillStyle = '#ffffff';
                  ctx.beginPath();
                  ctx.arc(clampedEnd.x, clampedEnd.y, 6, 0, Math.PI * 2);
                  ctx.fill();
                  ctx.strokeStyle = color;
                  ctx.lineWidth = 2;
                  ctx.stroke();
                }
              }
            } else {
              // 还没点击第一个点，只显示吸附点
              ctx.fillStyle = '#ffffff';
              ctx.beginPath();
              ctx.arc(snapResult.snappedPoint.x, snapResult.snappedPoint.y, 6, 0, Math.PI * 2);
              ctx.fill();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2;
              ctx.stroke();
            }
          }
        }
      }
    }

    // 3D模型放置预览 - 显示实际模型边缘线
    if (currentTool === ToolType.MODEL_3D && model3DPlacement && mouseWorldPos) {
      const halfWidth = model3DPlacement.width / 2;
      const halfDepth = model3DPlacement.depth / 2;
      
      ctx.save();
      
      // 绘制淡色背景框
      ctx.fillStyle = 'rgba(59, 130, 246, 0.05)';
      ctx.fillRect(
        mouseWorldPos.x - halfWidth,
        mouseWorldPos.y - halfDepth,
        model3DPlacement.width,
        model3DPlacement.depth
      );
      
      // 如果已加载模型边缘线，绘制实际轮廓
      if (model3DOutline && model3DOutline.lines.length > 0) {
        drawModel3DOutline(
          ctx,
          model3DOutline,
          mouseWorldPos.x,
          mouseWorldPos.y,
          '#174a72',
          0
        );
      } else {
        // 加载中，显示简单的3D图标
        const iconSize = Math.min(model3DPlacement.width, model3DPlacement.depth) * 0.25;
        const cubeSize = iconSize * 0.7;
        const cubeX = mouseWorldPos.x - cubeSize / 2;
        const cubeY = mouseWorldPos.y - cubeSize / 2;
        const offset3D = cubeSize * 0.25;
        
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // 前面
        ctx.strokeRect(cubeX, cubeY, cubeSize, cubeSize);
        
        // 顶面
        ctx.beginPath();
        ctx.moveTo(cubeX, cubeY);
        ctx.lineTo(cubeX + offset3D, cubeY - offset3D);
        ctx.lineTo(cubeX + cubeSize + offset3D, cubeY - offset3D);
        ctx.lineTo(cubeX + cubeSize, cubeY);
        ctx.stroke();
        
        // 右面
        ctx.beginPath();
        ctx.moveTo(cubeX + cubeSize, cubeY);
        ctx.lineTo(cubeX + cubeSize + offset3D, cubeY - offset3D);
        ctx.lineTo(cubeX + cubeSize + offset3D, cubeY + cubeSize - offset3D);
        ctx.lineTo(cubeX + cubeSize, cubeY + cubeSize);
        ctx.stroke();
        
        // 显示加载提示
        ctx.font = '9px Arial';
        ctx.fillStyle = '#94a3b8';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText('加载中...', mouseWorldPos.x, mouseWorldPos.y + cubeSize / 2 + 8);
      }
      
      // 绘制边框
      ctx.strokeStyle = '#1e5a8a';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        mouseWorldPos.x - halfWidth,
        mouseWorldPos.y - halfDepth,
        model3DPlacement.width,
        model3DPlacement.depth
      );
      ctx.setLineDash([]);
      
      // 绘制中心点
      ctx.fillStyle = '#1e5a8a';
      ctx.beginPath();
      ctx.arc(mouseWorldPos.x, mouseWorldPos.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
      
      // 绘制模型名称标签（在上方）
      ctx.font = '11px Arial';
      ctx.fillStyle = '#1e40af';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(model3DPlacement.modelName, mouseWorldPos.x, mouseWorldPos.y - halfDepth - 6);
      
      // 绘制尺寸标注（在下方）- 显示真实尺寸
      ctx.font = '9px Arial';
      ctx.fillStyle = '#64748b';
      ctx.textBaseline = 'top';
      const widthM = (model3DPlacement.realWidth / 1000).toFixed(2);
      const depthM = (model3DPlacement.realDepth / 1000).toFixed(2);
      ctx.fillText(
        `${widthM}m × ${depthM}m`,
        mouseWorldPos.x,
        mouseWorldPos.y + halfDepth + 4
      );
      
      ctx.restore();
    }

    // 恢复状态
    ctx.restore();
  }, [
    width,
    height,
    offset,
    zoom,
    project,
    currentFloor,
    tempPoints,
    isDrawing,
    currentTool,
    selectedElements,
    hoveredElement,
    wallProperties,
    mouseWorldPos,
    doorWindowDrawing,
    pendingDoor,
    model3DPlacement,
    worldToScreen,
    snapToWall,
  ]);

  // 创建未节流的render函数别名（用于依赖数组）
  const render = renderThrottled;

  // 自动渲染
  useEffect(() => {
    render();
  }, [render]);
  
  // 键盘快捷键处理（方向键微调 + Ctrl+V粘贴）
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 检查是否在输入框中
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      
      // Ctrl+V 粘贴到鼠标位置
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        if (clipboard.length > 0 && mouseWorldPos) {
          pasteElements(mouseWorldPos);
        }
        return;
      }
      
      // 方向键微调（只在选择工具且有选中元素时响应）
      if (currentTool !== ToolType.SELECT || selectedElements.length === 0) return;
      
      const moveStep = 1; // 每次移动1像素，提供精细控制
      let dx = 0;
      let dy = 0;
      
      switch (e.key) {
        case 'ArrowUp':
          dy = -moveStep;
          e.preventDefault();
          break;
        case 'ArrowDown':
          dy = moveStep;
          e.preventDefault();
          break;
        case 'ArrowLeft':
          dx = -moveStep;
          e.preventDefault();
          break;
        case 'ArrowRight':
          dx = moveStep;
          e.preventDefault();
          break;
        default:
          return;
      }
      
      // 移动所有选中的元素
      if (!project || !currentFloor) return;
      
      const floor = project.floors.find(f => f.id === currentFloor);
      if (!floor) return;
      
      selectedElements.forEach(elementId => {
        // 查找元素
        let element = null;
        for (const layer of floor.layers) {
          const found = layer.elements.find(el => el.id === elementId);
          if (found) {
            element = found;
            break;
          }
        }
        
        if (element && !element.locked) {
          // 移动所有点
          const newPoints = element.points.map(p => ({
            x: p.x + dx,
            y: p.y + dy
          }));
          updateElement(elementId, { points: newPoints });
        }
      });
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTool, selectedElements, project, currentFloor, updateElement, clipboard, mouseWorldPos, pasteElements]);
  
  // 监听底图加载或校准完成，自动居中显示
  // 注意：监听 baseMap 本身（通过 project.mapConfig 引用变化），在上传底图后立即居中
  // 然后在校准完成后（isCalibrated 变化）再次居中（使用正确的 scale）
  useEffect(() => {
    const baseMap = project?.mapConfig?.baseMap;
    const isCalibrated = project?.mapConfig?.isCalibrated;

    console.log('🔍 底图居中useEffect触发:', {
      hasBaseMap: !!baseMap,
      isCalibrated,
      canvasSize: { width, height },
      baseMapScale: baseMap?.scale
    });

    // 底图存在时才可能居中
    if (!baseMap) {
      console.log('⚠️ 跳过居中: 底图不存在');
      return;
    }

    // 防止重复居中（但每次校准完成后会重新居中一次）
    // 只有当 isCalibrated 为 true 时才使用 hasCenteredRef 防止重复
    if (isCalibrated && hasCenteredRef.current) {
      console.log('⏭️ 已居中过（已校准），跳过');
      return;
    }

    // 每次底图变化时（非校准完成），都重新计算居中
    // 这样上传底图后（未校准）会居中，校准完成后也会居中
    if (!isCalibrated) {
      console.log('📍 底图已上传（未校准），执行初始居中');
      hasCenteredRef.current = false;
    }

    console.log('🎯 开始居中计算:', {
      baseMapSize: { width: baseMap.width, height: baseMap.height },
      baseMapOffset: baseMap.offset,
      baseMapScale: baseMap.scale
    });

    // 计算底图在世界坐标系中的尺寸（与 BaseMapRenderer 保持一致）
    // worldWidth = 像素 * (100 / scale) = 像素 / calibrationScale
    const worldWidth = baseMap.width * (100 / baseMap.scale);
    const worldHeight = baseMap.height * (100 / baseMap.scale);

    // 计算底图中心点（世界坐标）
    const baseMapCenterX = baseMap.offset.x + worldWidth / 2;
    const baseMapCenterY = baseMap.offset.y + worldHeight / 2;

    // 计算合适的缩放比例（让底图占画布80%）
    const scaleX = (width * 0.8) / worldWidth;
    const scaleY = (height * 0.8) / worldHeight;
    const newZoom = Math.min(scaleX, scaleY);

    // 计算偏移量，使底图中心对齐到画布中心
    const newOffsetX = width / 2 - baseMapCenterX * newZoom;
    const newOffsetY = height / 2 - baseMapCenterY * newZoom;

    console.log('🎯 底图居中计算:', {
      worldSize: { width: worldWidth.toFixed(2), height: worldHeight.toFixed(2) },
      baseMapCenter: { x: baseMapCenterX.toFixed(2), y: baseMapCenterY.toFixed(2) },
      canvasSize: { width, height },
      newZoom: newZoom.toFixed(4),
      newOffset: { x: newOffsetX.toFixed(2), y: newOffsetY.toFixed(2) }
    });

    // 同步执行，不需要 setTimeout 延迟
    setZoom(newZoom);
    setOffset({ x: newOffsetX, y: newOffsetY });
    if (onZoomChange) onZoomChange(newZoom);
    if (onOffsetChange) onOffsetChange({ x: newOffsetX, y: newOffsetY });

    // 如果已校准完成，标记居中完成
    if (isCalibrated) {
      hasCenteredRef.current = true;
    }

    // 标记底图居中完成
    setBaseMapCentered(true);

    console.log('✅ 底图居中完成', isCalibrated ? '（已校准）' : '（未校准）');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.mapConfig?.baseMap, project?.mapConfig?.isCalibrated, width, height]); // 监听底图和校准状态
  
  // 监听ESC键取消门窗绘制
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && doorWindowDrawing) {
        setDoorWindowDrawing(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [doorWindowDrawing, setDoorWindowDrawing]);

  // 加载3D模型边缘线（当进入放置模式时）
  useEffect(() => {
    if (model3DPlacement && model3DPlacement.modelPath) {
      setModel3DOutline(null); // 先清空
      loadModel3DOutline(
        model3DPlacement.modelPath,
        model3DPlacement.width,
        model3DPlacement.depth
      ).then((outline) => {
        if (outline) {
          setModel3DOutline(outline);
          console.log(`✅ 模型边缘线加载成功: ${outline.lines.length} 条线段`);
        }
      }).catch((error) => {
        console.error('❌ 加载模型边缘线失败:', error);
        setModel3DOutline(null);
      });
    } else {
      setModel3DOutline(null);
    }
  }, [model3DPlacement]);



  // 处理鼠标点击
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPoint = screenToWorld(screenX, screenY);

    // 中键、Shift键或空格键 + 鼠标 = 平移
    if (e.button === 1 || e.shiftKey || isSpacePressed) {
      setIsPanning(true);
      setPanStart({ x: screenX, y: screenY });
      return;
    }

    // 根据当前工具处理点击
    switch (currentTool) {
      case ToolType.SELECT:
        handleSelectTool(worldPoint, e.ctrlKey || e.metaKey);
        break;

      case ToolType.WALL:
      case ToolType.AREA:
      case ToolType.POLYGON:
        // 多点绘制工具
        let snappedPoint = snapToGrid(worldPoint, GRID_SNAP_SIZE);
        
        // 墙面工具应用特殊吸附
        if (currentTool === ToolType.WALL) {
          // 优先吸附到有效区域边界
          const validArea = project?.mapConfig?.validArea;
          if (validArea && validArea.length >= 3) {
            const validAreaSnap = snapToValidArea(worldPoint, validArea, 30 / zoom);
            if (validAreaSnap.snapped) {
              snappedPoint = validAreaSnap.point;
            }
          }
          
          // 应用角度吸附
          if (tempPoints.length > 0) {
            const lastPoint = tempPoints[tempPoints.length - 1];
            
            if (wallProperties.useCustomAngle && wallProperties.customAngle !== null) {
              // 使用自定义角度吸附
              const angleRad = (wallProperties.customAngle * Math.PI) / 180;
              const dx = snappedPoint.x - lastPoint.x;
              const dy = snappedPoint.y - lastPoint.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              // 计算目标角度的坐标
              snappedPoint = {
                x: lastPoint.x + distance * Math.cos(angleRad),
                y: lastPoint.y + distance * Math.sin(angleRad),
              };
            } else {
              // 使用默认90度倍数±5度吸附
              snappedPoint = snapToAngle(lastPoint, snappedPoint, 5);
            }
          }
        }
        
        // 检查是否点击了起点（至少需要3个点才能形成闭合）
        if (tempPoints.length >= 3) {
          const firstPoint = tempPoints[0];
          const distance = Math.sqrt(
            Math.pow(snappedPoint.x - firstPoint.x, 2) + 
            Math.pow(snappedPoint.y - firstPoint.y, 2)
          );
          if (distance < 20 / zoom) {
            // 先添加闭合点（起点），完成最后一段的绘制
            addTempPoint(firstPoint);
            
            // 计算起点的屏幕坐标并显示确认对话框
            const screenPos = worldToScreen(firstPoint.x, firstPoint.y);
            // 浮窗显示在点的上方居中，添加边界检测
            const preferredPos = adjustPanelPosition(
              screenPos.x - CONFIRM_DIALOG_WIDTH / 2,
              screenPos.y - CONFIRM_DIALOG_HEIGHT - FLOATING_PANEL_OFFSET,
              CONFIRM_DIALOG_WIDTH,
              CONFIRM_DIALOG_HEIGHT,
              width,
              height
            );
            setConfirmDialog({
              message: t('canvas.closeConfirm'),
              position: preferredPos,
            });
            break;
          }
        }
        
        addTempPoint(snappedPoint);
        break;

      case ToolType.RECTANGLE:
      case ToolType.CIRCLE:
      case ToolType.STAIRS:
        // 拖拽绘制工具
        const snappedStart = snapToGrid(worldPoint, GRID_SNAP_SIZE);
        addTempPoint(snappedStart);
        break;
      
      case ToolType.MODEL_3D:
        // 3D模型放置工具
        if (model3DPlacement) {
          const snappedPoint = snapToGrid(worldPoint, GRID_SNAP_SIZE);
          placeModel3D(snappedPoint, model3DOutline || undefined);
        }
        break;
      
      case ToolType.TEXT:
        // 文本工具：点击位置打开输入对话框
        const textPoint = snapToGrid(worldPoint, GRID_SNAP_SIZE);
        setPendingText(textPoint);
        break;

      case ToolType.DOOR:
        // 门工具：点击模式
        if (!project || !currentFloor) break;
        
        const floor = project.floors.find(f => f.id === currentFloor);
        if (!floor) break;
        
        // 获取所有墙面
        const walls: any[] = [];
        for (const layer of floor.layers) {
          if (!layer.visible) continue;
          for (const element of layer.elements) {
            if (element.type === 'wall') {
              walls.push(element);
            }
          }
        }
        
        // 吸附到墙面
        const snapResult = snapToWall(worldPoint, walls, 30 / zoom);
        if (snapResult) {
          if (!doorWindowDrawing) {
            // 第一次点击：记录起点
            setDoorWindowDrawing({
              wallId: snapResult.wallId,
              wallSegmentIndex: snapResult.wallSegmentIndex,
              startPoint: snapResult.snappedPoint,
            });
          } else if (doorWindowDrawing.wallId === snapResult.wallId && 
                     doorWindowDrawing.wallSegmentIndex === snapResult.wallSegmentIndex) {
            // 第二次点击：完成绘制
            setPendingDoor({
              wallId: doorWindowDrawing.wallId,
              wallSegmentIndex: doorWindowDrawing.wallSegmentIndex,
              point1: doorWindowDrawing.startPoint,
              point2: snapResult.snappedPoint,
            });
          } else {
            // 点击了不同的墙面线段，重新开始
            setDoorWindowDrawing({
              wallId: snapResult.wallId,
              wallSegmentIndex: snapResult.wallSegmentIndex,
              startPoint: snapResult.snappedPoint,
            });
          }
        }
        break;

      default:
        break;
    }
  };

  // 处理Model3D元素的等比例缩放
  const handleModel3DResize = (element: any, _: number, newCornerPos: Point2D) => {
    // 获取旋转角度
    const rotation = element.rotation || 0;
    const rotationRad = (rotation * Math.PI) / 180;

    // 计算当前边界框的中心点（使用所有points的平均值，与3D预览保持一致）
    const centerX = element.points.reduce((sum: number, p: Point2D) => sum + p.x, 0) / element.points.length;
    const centerY = element.points.reduce((sum: number, p: Point2D) => sum + p.y, 0) / element.points.length;

    // 获取原始未旋转的尺寸
    const oldWidth = element.width || (element.points[1].x - element.points[0].x);
    const oldHeight = element.depth || (element.points[2].y - element.points[1].y);

    // 将新角点位置转换到未旋转的坐标系
    // 1. 平移到原点
    const dx = newCornerPos.x - centerX;
    const dy = newCornerPos.y - centerY;

    // 2. 反向旋转
    const cos = Math.cos(-rotationRad);
    const sin = Math.sin(-rotationRad);
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;

    // 3. 计算新的半宽和半高（基于拖拽的角点位置）
    // 使用绝对值确保尺寸为正，并统一使用正半轴值
    let newHalfWidth = Math.abs(localX);
    let newHalfHeight = Math.abs(localY);

    // 等比例缩放：保持宽度和深度的原始比例
    const aspectRatio = oldWidth / oldHeight;
    const currentAspectRatio = newHalfWidth / newHalfHeight;

    if (currentAspectRatio > aspectRatio) {
      // 宽度变化更大，按宽度等比例调整高度
      newHalfHeight = newHalfWidth / aspectRatio;
    } else {
      // 高度变化更大，按高度等比例调整宽度
      newHalfWidth = newHalfHeight * aspectRatio;
    }

    // 计算新的完整尺寸
    const newWidth = newHalfWidth * 2;
    const newHeight = newHalfHeight * 2;

    // 计算未旋转的新边界框（相对于中心点）
    const halfWidth = newWidth / 2;
    const halfHeight = newHeight / 2;

    // 创建未旋转的四个角点
    const unrotatedCorners: Point2D[] = [
      { x: -halfWidth, y: -halfHeight },
      { x: halfWidth, y: -halfHeight },
      { x: halfWidth, y: halfHeight },
      { x: -halfWidth, y: halfHeight },
    ];

    // 应用旋转并转换到世界坐标
    const cosRot = Math.cos(rotationRad);
    const sinRot = Math.sin(rotationRad);
    const newPoints: Point2D[] = unrotatedCorners.map(p => ({
      x: centerX + (p.x * cosRot - p.y * sinRot),
      y: centerY + (p.x * sinRot + p.y * cosRot),
    }));

    // 验证：计算新points的中心点，确保与原始中心点一致
    const newCenterX = newPoints.reduce((sum, p) => sum + p.x, 0) / newPoints.length;
    const newCenterY = newPoints.reduce((sum, p) => sum + p.y, 0) / newPoints.length;

    // 如果中心点有偏差，强制校正
    if (Math.abs(newCenterX - centerX) > 0.001 || Math.abs(newCenterY - centerY) > 0.001) {
      const offsetX = centerX - newCenterX;
      const offsetY = centerY - newCenterY;
      newPoints.forEach(p => {
        p.x += offsetX;
        p.y += offsetY;
      });
    }

    // 更新元素
    // 同时更新realWidth和realDepth，保持比例
    const calibrationScale = project?.mapConfig?.calibrationScale || 10;
    const newRealWidth = (newWidth / calibrationScale) * 10; // px → cm → mm
    const newRealDepth = (newHeight / calibrationScale) * 10; // px → cm → mm

    updateElement(element.id, {
      points: newPoints,
      width: newWidth,
      depth: newHeight,
      realWidth: newRealWidth,
      realDepth: newRealDepth,
    });
  };

  // 处理选择工具
  const handleSelectTool = (worldPoint: Point2D, ctrlKey: boolean = false) => {
    if (!project || !currentFloor) return;

    const floor = project.floors.find((f) => f.id === currentFloor);
    if (!floor) return;

    // 先检查是否点击了选中元素的旋转区域（四个角外侧）- Photoshop风格
    for (const selectedId of selectedElements) {
      let element = null;
      for (const layer of floor.layers) {
        const found = layer.elements.find(el => el.id === selectedId);
        if (found) {
          element = found;
          break;
        }
      }

      if (element && element.points.length > 0 && !element.locked && element.type !== 'model3d') {
        // 计算边界框和中心点
        const minX = Math.min(...element.points.map(p => p.x));
        const maxX = Math.max(...element.points.map(p => p.x));
        const minY = Math.min(...element.points.map(p => p.y));
        const maxY = Math.max(...element.points.map(p => p.y));
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // 计算元素的四个角
        const corners = [
          { x: minX, y: minY },
          { x: maxX, y: minY },
          { x: maxX, y: maxY },
          { x: minX, y: maxY },
        ];

        // 检测鼠标是否在角点外侧的旋转区域（15像素热区）
        const rotationHotzone = 15;
        for (const corner of corners) {
          const dist = Math.sqrt(
            Math.pow(worldPoint.x - corner.x, 2) +
            Math.pow(worldPoint.y - corner.y, 2)
          );

          if (dist < rotationHotzone) {
            // 开始旋转
            const startAngle = Math.atan2(worldPoint.y - centerY, worldPoint.x - centerX);
            setRotateState({
              elementId: element.id,
              startAngle: startAngle * 180 / Math.PI,
              centerPoint: { x: centerX, y: centerY },
            });
            return;
          }
        }
      }
    }

    // 检查是否点击了旋转手柄（fabric.js风格位置）- 保留兼容
    // 注意：此功能未完整实现，暂跳过后续检查
    // 先检查是否点击了端点（用于拖拽）
    for (const layer of floor.layers) {
      if (!layer.visible || layer.locked) continue;

      for (const element of layer.elements) {
        if (element.locked) continue;
        
        let pointIndex: number | null = null;
        
        // 对于model3d元素，需要考虑旋转后的角点位置
        if (element.type === 'model3d' && element.rotation) {
          const minX = Math.min(...element.points.map(p => p.x));
          const maxX = Math.max(...element.points.map(p => p.x));
          const minY = Math.min(...element.points.map(p => p.y));
          const maxY = Math.max(...element.points.map(p => p.y));
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          
          const rotationRad = (element.rotation * Math.PI) / 180;
          const cos = Math.cos(rotationRad);
          const sin = Math.sin(rotationRad);
          
          // 计算旋转后的角点位置
          const rotatedPoints = element.points.map(p => {
            const dx = p.x - centerX;
            const dy = p.y - centerY;
            return {
              x: centerX + (dx * cos - dy * sin),
              y: centerY + (dx * sin + dy * cos),
            };
          });
          
          // 检测是否点击了旋转后的角点
          for (let i = 0; i < rotatedPoints.length; i++) {
            const p = rotatedPoints[i];
            const dist = Math.sqrt(Math.pow(worldPoint.x - p.x, 2) + Math.pow(worldPoint.y - p.y, 2));
            if (dist < 8 / zoom) {
              pointIndex = i;
              break;
            }
          }
        } else {
          // 其他元素使用原来的检测方法
          pointIndex = getPointNearEndpoint(worldPoint, element, 8 / zoom);
        }
        
        if (pointIndex !== null) {
          // 点击了端点，开始拖拽
          setDragState({ elementId: element.id, pointIndex });

          // 对于矩形、圆形、多边形等可缩放元素，点击端点时不自动选中
          // 只有点击元素本身才选中，避免选中后锚点变色无法继续缩放
          if (!ctrlKey && element.type !== 'rectangle' && element.type !== 'circle' && element.type !== 'polygon') {
            selectElement(element.id);
          }

          setRightPanelTab('elements');
          return;
        }
      }
    }

    // 检查是否点击了元素
    let found = false;
    let clickedElement = null;
    for (const layer of floor.layers) {
      if (!layer.visible) continue;

      for (const element of layer.elements) {
        if (element.locked) continue;
        if (isPointOnElement(worldPoint, element, 10 / zoom)) {
          clickedElement = element;
          found = true;
          break;
        }
      }
      if (found) break;
    }

    if (found && clickedElement) {
      // Ctrl多选
      if (ctrlKey) {
        console.log('CTRL+点击元素:', clickedElement.id);
        console.log('当前选中:', selectedElements);
        if (selectedElements.includes(clickedElement.id)) {
          // 已选中，取消选中
          const newSelection = selectedElements.filter(id => id !== clickedElement.id);
          console.log('取消选中，新选择:', newSelection);
          selectMultipleElements(newSelection);
        } else {
          // 添加到选择列表
          const newSelection = [...selectedElements, clickedElement.id];
          console.log('添加到选择，新选择:', newSelection);
          selectMultipleElements(newSelection);
        }
      } else {
        // 单选并开始拖拽移动
        if (!selectedElements.includes(clickedElement.id)) {
          selectElement(clickedElement.id);
        }
        // 开始拖拽移动元素
        setElementDragState({
          elementId: clickedElement.id,
          startPoint: worldPoint,
          originalPoints: [...clickedElement.points],
        });
      }
      setRightPanelTab('elements');
    } else {
      // 如果没有点击到元素，清除选择（除非按住Ctrl）
      if (!ctrlKey) {
        clearSelection();
      }
    }
  };

  // 处理鼠标移动
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPoint = screenToWorld(screenX, screenY);

    // 更新鼠标世界坐标（用于预览）
    setMouseWorldPos(worldPoint);

    // 平移模式
    if (isPanning) {
      const dx = screenX - panStart.x;
      const dy = screenY - panStart.y;
      const newOffset = {
        x: offset.x + dx,
        y: offset.y + dy,
      };
      setOffset(newOffset);
      onOffsetChange?.(newOffset);
      setPanStart({ x: screenX, y: screenY });
      return;
    }

    // 端点拖拽中
    if (dragState && project && currentFloor) {
      const snappedPoint = snapToGrid(worldPoint, GRID_SNAP_SIZE);

      // 检查是否是 model3d 元素
      const floor = project.floors.find(f => f.id === currentFloor);
      if (floor) {
        let element = null;
        for (const layer of floor.layers) {
          const found = layer.elements.find(el => el.id === dragState.elementId);
          if (found) {
            element = found;
            break;
          }
        }

        // 如果是 model3d 元素，使用等比例缩放
        if (element && element.type === 'model3d') {
          handleModel3DResize(element as any, dragState.pointIndex, snappedPoint);
          return;
        }

        // 如果是 rectangle 元素，需要处理缩放（保持中心点不变）
        if (element && element.type === 'rectangle' && element.points.length === 4) {
          const rect = element as any;
          const minX = Math.min(...rect.points.map((p: any) => p.x));
          const maxX = Math.max(...rect.points.map((p: any) => p.x));
          const minY = Math.min(...rect.points.map((p: any) => p.y));
          const maxY = Math.max(...rect.points.map((p: any) => p.y));

          // 计算新尺寸（基于拖拽的点）
          let newMinX = minX;
          let newMaxX = maxX;
          let newMinY = minY;
          let newMaxY = maxY;

          switch (dragState.pointIndex) {
            case 0: // 左上角
              newMinX = snappedPoint.x;
              newMinY = snappedPoint.y;
              break;
            case 1: // 右上角
              newMaxX = snappedPoint.x;
              newMinY = snappedPoint.y;
              break;
            case 2: // 右下角
              newMaxX = snappedPoint.x;
              newMaxY = snappedPoint.y;
              break;
            case 3: // 左下角
              newMinX = snappedPoint.x;
              newMaxY = snappedPoint.y;
              break;
          }

          const newPoints: Point2D[] = [
            { x: newMinX, y: newMinY },
            { x: newMaxX, y: newMinY },
            { x: newMaxX, y: newMaxY },
            { x: newMinX, y: newMaxY },
          ];

          // 更新宽度和深度（厘米值）
          const newWidth = Math.abs(newMaxX - newMinX) * 100;
          const newDepth = Math.abs(newMaxY - newMinY) * 100;

          updateElement(dragState.elementId, { points: newPoints, width: newWidth, depth: newDepth });
          return;
        }

        // 如果是 circle 元素，需要保持中心点不变，只更新半径
        if (element && element.type === 'circle' && element.points.length === 2) {
          const circle = element as any;
          const center = circle.points[0];
          const newRadius = Math.sqrt(
            Math.pow(snappedPoint.x - center.x, 2) + Math.pow(snappedPoint.y - center.y, 2)
          );
          const newPoints: Point2D[] = [
            center,
            { x: center.x + newRadius, y: center.y },
          ];
          updateElement(dragState.elementId, { points: newPoints, radius: newRadius * 100 });
          return;
        }
      }

      updateElementPoint(dragState.elementId, dragState.pointIndex, snappedPoint);
      return;
    }

    // 元素拖拽移动中
    if (elementDragState && project && currentFloor) {
      const snappedPoint = snapToGrid(worldPoint, GRID_SNAP_SIZE);
      const dx = snappedPoint.x - elementDragState.startPoint.x;
      const dy = snappedPoint.y - elementDragState.startPoint.y;
      
      const newPoints = elementDragState.originalPoints.map(p => ({
        x: p.x + dx,
        y: p.y + dy,
      }));
      
      updateElement(elementDragState.elementId, { points: newPoints });
      return;
    }

    // 更新悬停元素和端点
    if (currentTool === ToolType.SELECT && project && currentFloor) {
      const floor = project.floors.find((f) => f.id === currentFloor);
      if (floor) {
        let hoveredId: string | null = null;
        let hoveredPt: { elementId: string; pointIndex: number } | null = null;

        // 先检查端点
        for (const layer of floor.layers) {
          if (!layer.visible) continue;
          for (const element of layer.elements) {
            const pointIndex = getPointNearEndpoint(worldPoint, element, 8 / zoom);
            if (pointIndex !== null) {
              hoveredPt = { elementId: element.id, pointIndex };
              hoveredId = element.id;
              break;
            }
          }
          if (hoveredPt) break;
        }

        // 如果没有悬停端点，检查元素
        if (!hoveredPt) {
          for (const layer of floor.layers) {
            if (!layer.visible) continue;
            for (const element of layer.elements) {
              if (isPointOnElement(worldPoint, element, 10 / zoom)) {
                hoveredId = element.id;
                break;
              }
            }
            if (hoveredId) break;
          }
        }

        setHoveredElement(hoveredId);
        setHoveredPoint(hoveredPt);
      }
    }

    // 墙面/区域/多边形工具 - 显示实时预览线
    if (
      isDrawing &&
      tempPoints.length >= 1 &&
      (currentTool === ToolType.WALL || 
       currentTool === ToolType.AREA || 
       currentTool === ToolType.POLYGON)
    ) {
      // 实时预览会在render中处理，这里只需要触发重绘
      render();
    }

    // 矩形/圆形/楼梯拖拽绘制 - 实时更新第二个点
    if (
      isDrawing &&
      tempPoints.length >= 1 &&
      (currentTool === ToolType.RECTANGLE || 
       currentTool === ToolType.CIRCLE ||
       currentTool === ToolType.STAIRS)
    ) {
      const snappedPoint = snapToGrid(worldPoint, GRID_SNAP_SIZE);
      if (tempPoints.length === 1) {
        // 添加第二个点用于预览
        addTempPoint(snappedPoint);
      } else {
        // 更新第二个点
        updateLastTempPoint(snappedPoint);
      }
    }
    
    // 门工具 - 实时触发重绘以显示吸附效果
    if (currentTool === ToolType.DOOR) {
      render();
    }
    
    // 3D模型放置工具 - 实时触发重绘以显示预览
    if (currentTool === ToolType.MODEL_3D && model3DPlacement) {
      render();
    }
  };

  // 处理鼠标抬起
  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    // 结束端点拖拽
    if (dragState) {
      setDragState(null);
      return;
    }

    // 结束元素拖拽移动
    if (elementDragState) {
      setElementDragState(null);
      return;
    }

    // 矩形家具工具 - 松开鼠标显示属性浮窗
    if (isDrawing && tempPoints.length >= 2 && currentTool === ToolType.RECTANGLE) {
      setPendingFurniture(tempPoints);
      return;
    }
    
    // 圆形工具 - 松开鼠标显示属性浮窗
    if (isDrawing && tempPoints.length >= 2 && currentTool === ToolType.CIRCLE) {
      setPendingCircle(tempPoints);
      return;
    }
    
    // 楼梯工具 - 松开鼠标显示属性浮窗
    if (isDrawing && tempPoints.length >= 2 && currentTool === ToolType.STAIRS) {
      setPendingStair(tempPoints);
      return;
    }
  };

  // 处理双击（完成多边形绘制）
  const handleDoubleClick = () => {
    if (isDrawing && tempPoints.length >= 3) {
      if (currentTool === ToolType.AREA) {
        // 区域工具：设置待命名状态，不直接创建元素
        setPendingArea(tempPoints);
      } else if (currentTool === ToolType.POLYGON) {
        // 多边形工具：显示属性浮窗
        setPendingPolygon(tempPoints);
      }
    }
  };

  // 处理滚轮缩放
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    // 如果正在校准且已经点击了第一个点，禁止缩放
    if (isCalibrating && calibrationPoint1) {
      console.log('⚠️ 校准模式下已点击第一个点，禁止缩放');
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // 缩放前的世界坐标
    const worldBefore = screenToWorld(mouseX, mouseY);

    // 更新缩放
    const zoomDelta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(5, zoom * zoomDelta));

    setZoom(newZoom);
    onZoomChange?.(newZoom);

    // 缩放后的世界坐标
    const worldAfter = {
      x: (mouseX - offset.x) / newZoom,
      y: (mouseY - offset.y) / newZoom,
    };

    // 调整偏移以保持鼠标位置的世界坐标不变
    const newOffset = {
      x: offset.x + (worldAfter.x - worldBefore.x) * newZoom,
      y: offset.y + (worldAfter.y - worldBefore.y) * newZoom,
    };
    setOffset(newOffset);
    onOffsetChange?.(newOffset);
  };

  // 处理键盘事件
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 空格键 - 开启平移模式
      if (e.code === 'Space' && !isSpacePressed) {
        e.preventDefault();
        setIsSpacePressed(true);
      }

      // ESC - 取消绘制或取消3D模型放置
      if (e.key === 'Escape') {
        if (isDrawing) {
          cancelDrawing();
        }
        if (model3DPlacement) {
          cancelModel3DPlacement();
        }
      }

      // Enter - 完成绘制
      if (e.key === 'Enter' && isDrawing) {
        finishDrawing();
      }

      // Delete - 删除选中元素
      if (e.key === 'Delete' && selectedElements.length > 0) {
        // TODO: 调用删除方法
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // 空格键释放 - 关闭平移模式
      if (e.code === 'Space') {
        setIsSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDrawing, selectedElements, isSpacePressed, cancelDrawing, finishDrawing]);

  // 处理右键菜单
  const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPoint = screenToWorld(screenX, screenY);

    if (!project) return;

    // 查找点击的元素
    const floor = project.floors.find((f) => f.id === currentFloor);
    if (!floor) return;

    let clickedElementId: string | null = null;

    for (const layer of floor.layers) {
      if (!layer.visible) continue;

      for (const element of layer.elements) {
        if (!element.visible) continue;

        // 检查鼠标是否在元素上
        if (isPointOnElement(worldPoint, element, 10 / zoom)) {
          clickedElementId = element.id;
          break;
        }
      }

      if (clickedElementId) break;
    }

    // 如果点击了元素，确保它被选中
    if (clickedElementId && !selectedElements.includes(clickedElementId)) {
      selectElement(clickedElementId);
    }

    // 显示右键菜单
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      elementId: clickedElementId,
    });
  };

  return (
    <div className="relative w-full h-full bg-gray-100 overflow-hidden">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={`absolute top-0 left-0 ${
          isPanning
            ? 'cursor-grabbing'
            : isSpacePressed
            ? 'cursor-grab'
            : dragState
            ? 'cursor-grabbing'
            : currentTool === ToolType.SELECT && hoveredPoint
            ? 'cursor-grab'
            : currentTool === ToolType.SELECT
            ? 'cursor-default'
            : 'cursor-crosshair'
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onContextMenu={handleContextMenu}
        data-canvas="2d"
        style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
      />

      {/* 墙面属性浮窗 */}
      {isDrawing && currentTool === ToolType.WALL && tempPoints.length > 0 && (
        <WallPropertyPanel
          position={(() => {
            const lastPoint = tempPoints[tempPoints.length - 1];
            const screenPos = worldToScreen(lastPoint.x, lastPoint.y);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH,
              FLOATING_PANEL_HEIGHT_WALL,
              width,
              height
            );
          })()}
          onConfirm={finishDrawing}
          onCancel={cancelDrawing}
        />
      )}

      {/* 区域命名浮窗 */}
      {pendingArea && (
        <AreaPropertyPanel
          position={(() => {
            const firstPoint = pendingArea.points[0];
            const screenPos = worldToScreen(firstPoint.x, firstPoint.y);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH_SMALL,
              FLOATING_PANEL_HEIGHT_AREA,
              width,
              height
            );
          })()}
          onConfirm={(name) => {
            createAreaWithName(name);
          }}
          onCancel={() => {
            setPendingArea(null);
            cancelDrawing();
          }}
        />
      )}

      {/* 家具属性浮窗 */}
      {pendingFurniture && (
        <FurniturePropertyPanel
          position={(() => {
            const firstPoint = pendingFurniture.points[0];
            const screenPos = worldToScreen(firstPoint.x, firstPoint.y);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH,
              FLOATING_PANEL_HEIGHT_FURNITURE,
              width,
              height
            );
          })()}
          initialWidth={Math.max(1, (Math.max(pendingFurniture.points[0].x, pendingFurniture.points[1].x) - Math.min(pendingFurniture.points[0].x, pendingFurniture.points[1].x)) * 100)}
          initialDepth={Math.max(1, (Math.max(pendingFurniture.points[0].y, pendingFurniture.points[1].y) - Math.min(pendingFurniture.points[0].y, pendingFurniture.points[1].y)) * 100)}
          onConfirm={(data) => {
            console.log(`📐 [矩形绘制] 确认尺寸: width=${data.width}cm, depth=${data.depth}cm`);
            createFurnitureWithProperties(data);
          }}
          onCancel={() => {
            setPendingFurniture(null);
            cancelDrawing();
          }}
        />
      )}

      {/* 圆形属性浮窗 */}
      {pendingCircle && (
        <CirclePropertyPanel
          position={(() => {
            const firstPoint = pendingCircle.points[0];
            const screenPos = worldToScreen(firstPoint.x, firstPoint.y);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH,
              FLOATING_PANEL_HEIGHT_CIRCLE,
              width,
              height
            );
          })()}
          onConfirm={(data) => {
            createCircleWithProperties(data);
          }}
          onCancel={() => {
            setPendingCircle(null);
            cancelDrawing();
          }}
        />
      )}

      {/* 多边形属性浮窗 */}
      {pendingPolygon && (
        <PolygonPropertyPanel
          position={(() => {
            const firstPoint = pendingPolygon.points[0];
            const screenPos = worldToScreen(firstPoint.x, firstPoint.y);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH,
              FLOATING_PANEL_HEIGHT_POLYGON,
              width,
              height
            );
          })()}
          onConfirm={(data) => {
            createPolygonWithProperties(data);
          }}
          onCancel={() => {
            setPendingPolygon(null);
            cancelDrawing();
          }}
        />
      )}

      {/* 门属性浮窗 */}
      {pendingDoor && (
        <DoorPropertyPanel
          position={(() => {
            const centerX = (pendingDoor.point1.x + pendingDoor.point2.x) / 2;
            const centerY = (pendingDoor.point1.y + pendingDoor.point2.y) / 2;
            const screenPos = worldToScreen(centerX, centerY);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH,
              FLOATING_PANEL_HEIGHT_DOOR,
              width,
              height
            );
          })()}
          onConfirm={(data) => {
            createDoorWithProperties(data);
          }}
          onCancel={() => {
            setPendingDoor(null);
          }}
        />
      )}

      {/* 楼梯属性浮窗 */}
      {pendingStair && (
        <StairPropertyPanel
          position={(() => {
            const firstPoint = pendingStair.points[0];
            const screenPos = worldToScreen(firstPoint.x, firstPoint.y);
            const preferredPos = {
              x: screenPos.x + FLOATING_PANEL_OFFSET,
              y: screenPos.y + FLOATING_PANEL_OFFSET,
            };
            return adjustPanelPosition(
              preferredPos.x,
              preferredPos.y,
              FLOATING_PANEL_WIDTH,
              FLOATING_PANEL_HEIGHT_STAIR,
              width,
              height
            );
          })()}
          onConfirm={(data) => {
            createStairWithProperties(data);
          }}
          onCancel={() => {
            setPendingStair(null);
            cancelDrawing();
          }}
        />
      )}

      {/* 完成绘制确认对话框 */}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          position={confirmDialog.position}
          onConfirm={() => {
            finishDrawing();
            setConfirmDialog(null);
          }}
          onCancel={() => {
            setConfirmDialog(null);
          }}
        />
      )}

      {/* 工具提示 */}
      <div className="absolute bottom-4 left-4 bg-white px-3 py-2 rounded shadow text-sm max-w-xs">
        <div className="font-medium">{t('canvas.zoom')}: {(zoom * 100).toFixed(0)}%</div>
        
        {/* 底图状态提示 */}
        {!project?.mapConfig?.baseMap && (
          <div className="text-xs text-amber-600 mt-1 font-medium">
            {t('canvas.needCAD')}
          </div>
        )}
        
        {project?.mapConfig?.baseMap && (
          <div className="text-xs text-green-600 mt-1">
            {t('canvas.cadLoaded')}
          </div>
        )}
        
        {/* 工具操作提示 */}
        <div className="text-xs text-gray-500 mt-1">
          {currentTool === ToolType.SELECT && t('canvas.clickToSelect')}
          {currentTool === ToolType.WALL && t('canvas.addPoint')}
          {currentTool === ToolType.AREA && t('canvas.drawArea')}
          {currentTool === ToolType.RECTANGLE && t('canvas.dragFurniture')}
          {currentTool === ToolType.CIRCLE && t('canvas.dragCircle')}
          {currentTool === ToolType.POLYGON && t('canvas.drawPolygon')}
          {currentTool === ToolType.DOOR && t('canvas.placeDoor')}
          {currentTool === ToolType.STAIRS && t('canvas.placeStair')}
          {currentTool === ToolType.TEXT && t('canvas.addText')}
          {currentTool === ToolType.MODEL_3D && model3DPlacement && `${t('canvas.placeModelTip')} "${model3DPlacement.modelName}", ${t('canvas.escToCancel')}`}
        </div>
      </div>

      {/* 文本输入对话框 */}
      {pendingText && (
        <TextInputDialog
          onConfirm={(data) => {
            createTextWithContent(data);
          }}
          onCancel={() => {
            setPendingText(null);
          }}
        />
      )}

      {/* 测量工具覆盖层 */}
      <MeasurementOverlay
        width={width}
        height={height}
        zoom={zoom}
        offset={offset}
      />

      {/* 右键菜单 */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={(() => {
            // 元素右键菜单
            if (contextMenu.elementId && selectedElements.length > 0) {
              // 获取选中的元素
              const floor = project?.floors.find(f => f.id === currentFloor);
              const selectedElementObjs = floor?.layers
                .flatMap(layer => layer.elements)
                .filter(el => selectedElements.includes(el.id)) || [];
              
              const firstElement = selectedElementObjs[0];
              if (!firstElement) return [];

              return createElementContextMenu({
                t,
                selectedCount: selectedElements.length,
                isLocked: firstElement.locked,
                isVisible: firstElement.visible,
                canAlign: true,
                onCopy: () => {
                  copySelectedElements();
                },
                onCut: () => {
                  cutSelectedElements();
                },
                onDelete: () => {
                  deleteSelectedElements();
                },
                onToggleLock: () => {
                  selectedElements.forEach(id => {
                    updateElement(id, { locked: !firstElement.locked });
                  });
                },
                onToggleVisibility: () => {
                  selectedElements.forEach(id => {
                    updateElement(id, { visible: !firstElement.visible });
                  });
                },
                onAlignLeft: selectedElements.length > 1 ? alignLeft : undefined,
                onAlignRight: selectedElements.length > 1 ? alignRight : undefined,
                onAlignTop: selectedElements.length > 1 ? alignTop : undefined,
                onAlignBottom: selectedElements.length > 1 ? alignBottom : undefined,
                onAlignVertical: selectedElements.length > 1 ? alignVerticalCenter : undefined,
                onAlignHorizontal: selectedElements.length > 1 ? alignHorizontalCenter : undefined,
              });
            }
            
            // 空白处右键菜单
            return createCanvasContextMenu({
              t,
              hasClipboard: clipboard.length > 0,
              onPaste: () => {
                // 将右键菜单的屏幕坐标转换为世界坐标
                if (contextMenu) {
                  const worldPos = screenToWorld(contextMenu.x, contextMenu.y);
                  pasteElements(worldPos);
                } else {
                  pasteElements();
                }
              },
              onSelectAll: () => {
                const floor = project?.floors.find(f => f.id === currentFloor);
                if (floor) {
                  const allElementIds = floor.layers
                    .flatMap(layer => layer.elements)
                    .map(el => el.id);
                  selectElement(allElementIds[0]);
                  allElementIds.slice(1).forEach(id => selectElement(id));
                }
              },
              onDeselectAll: () => {
                clearSelection();
              },
            });
          })()}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default Canvas2D;
